import { BaseResponseProps, HeaderSection } from '@qlibs/react-components';
import { DeviceProperties, initialDevice } from './types/device.type';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { backToList } from '../../helpers/listFilterQuery';
import useDetailBreadcrumbs from '../../hooks/useDetailBreadcrumbs';
import React, { useEffect, useMemo, useState } from 'react';
import { httpRequest } from '../../helpers/api';
import {
  Alert,
  Button,
  Grid,
  Space,
  Spin,
  Tabs,
  TabsProps,
  Tag,
  message,
} from 'antd';
import DeviceDetailInformation from './components/DetailData';
import DeviceDataLog from './components/DeviceDataLog';
import PanelDoorDataLog from './components/PanelDoorDataLog';
import {
  aeColumns,
  aeColumnsCustom,
  clutchCenterColumns,
  clutchPortColumns,
  clutchStarboardColumns,
  fmColumns,
  gatewayColumns,
  gpsColumns,
  rpmPortColumns,
  rpmStarboardColumns,
} from './components/DataColumns';
import {
  aeSchema,
  aeSchemaCustom,
  aeYAxis,
  aeYAxisCustom,
  clutchCenterSchema,
  clutchPortSchema,
  clutchStarboardSchema,
  clutchYAxis,
  fmSchema,
  fmYAxis,
  gatewaySchema,
  gatewayYAxis,
  gpsSchema,
  gpsYAxis,
  rpmPortSchema,
  rpmStarboardSchema,
  rpmYAxis,
} from './components/DataChartSchema';
import { isRamus2DeviceId } from '../../helpers/ramus2';
import {
  DeviceMobileActionButton,
  DeviceMobileAlert,
  DeviceMobileDetailLabel,
  DeviceMobileDetailRow,
  DeviceMobileDetailRows,
  DeviceMobileDetailSection,
  DeviceMobileDetailSectionHeader,
  DeviceMobileDetailTabs,
  DeviceMobileDetailValue,
  DeviceMobilePage,
  MobileDeviceHeader,
} from './mobile';
import moment from 'moment';

interface ILocation {
  deviceId: string;
}

interface ResponseProps
  extends BaseResponseProps<
    Omit<DeviceProperties, 'createdAt' | 'updatedAt' | 'companies'>
  > {}

const DeviceDetail = () => {
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const { setBreadcrumbDetails } = useDetailBreadcrumbs();
  const { deviceId } = useParams<keyof ILocation>() as ILocation;
  const isReadOnlyRamus2 = isRamus2DeviceId(deviceId);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [data, setData] = React.useState<DeviceProperties>({
    ...initialDevice,
  });
  React.useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);

        const res = await httpRequest.get<ResponseProps>('/device/' + deviceId);
        if (!res) {
          message.error('Something went wrong');
          return;
        }

        setData({
          ...res.data.payload,
        });

        let label = `Detail Device`;
        const bcDetails = [
          {
            field: 'deviceId',
            value: deviceId,
            label,
          },
        ];
        setBreadcrumbDetails(bcDetails);

        setIsLoading(false);
      } catch (error: any) {
        console.error(error);
        setIsLoading(false);
      }
    };

    fetchData();

    // eslint-disable-next-line
  }, [deviceId]);

  const handleClickEdit = () => {
    navigate('/device/' + deviceId + '/edit');
  };

  const chartData = useMemo(() => {
    if (data.deviceType.name.toLowerCase() === 'gps') {
      return { columns: gpsColumns, schema: gpsSchema, yAxis: gpsYAxis };
    } else if (
      data.deviceType.name.toLowerCase() === 'rpm' &&
      data.uniqueId.includes('rpm-1')
    ) {
      return {
        columns: rpmPortColumns,
        schema: rpmPortSchema,
        yAxis: rpmYAxis,
      };
    } else if (
      data.deviceType.name.toLowerCase() === 'rpm' &&
      data.uniqueId.includes('rpm-2')
    ) {
      return {
        columns: rpmStarboardColumns,
        schema: rpmStarboardSchema,
        yAxis: rpmYAxis,
      };
    } else if (
      data.deviceType.name.toLowerCase() === 'clutch' &&
      data.uniqueId.includes('clutch-1')
    ) {
      return {
        columns: clutchPortColumns,
        schema: clutchPortSchema,
        yAxis: clutchYAxis,
      };
    } else if (
      data.deviceType.name.toLowerCase() === 'clutch' &&
      data.uniqueId.includes('clutch-2')
    ) {
      return {
        columns: clutchStarboardColumns,
        schema: clutchStarboardSchema,
        yAxis: clutchYAxis,
      };
    } else if (
      data.deviceType.name.toLowerCase() === 'clutch' &&
      data.uniqueId.includes('clutch-3')
    ) {
      return {
        columns: clutchCenterColumns,
        schema: clutchCenterSchema,
        yAxis: clutchYAxis,
      };
    } else if (data.deviceType.name.toLowerCase() === 'flowmeter') {
      let currSchema;
      if (data.uniqueId.includes('fm-1-1')) {
        currSchema = fmSchema.map((item) => {
          return {
            ...item,
            dataKeys: ['portIn', ...item.dataKeys],
          };
        });
      } else if (data.uniqueId.includes('fm-1-2')) {
        currSchema = fmSchema.map((item) => {
          return {
            ...item,
            dataKeys: ['portOut', ...item.dataKeys],
          };
        });
      } else if (data.uniqueId.includes('fm-2-1')) {
        currSchema = fmSchema.map((item) => {
          return {
            ...item,
            dataKeys: ['starboardIn', ...item.dataKeys],
          };
        });
      } else if (data.uniqueId.includes('fm-2-2')) {
        currSchema = fmSchema.map((item) => {
          return {
            ...item,
            dataKeys: ['starboardOut', ...item.dataKeys],
          };
        });
      } else if (data.uniqueId.includes('fm-3-1')) {
        currSchema = fmSchema.map((item) => {
          return {
            ...item,
            dataKeys: ['centerIn', ...item.dataKeys],
          };
        });
      } else if (data.uniqueId.includes('fm-3-2')) {
        currSchema = fmSchema.map((item) => {
          return {
            ...item,
            dataKeys: ['centerOut', ...item.dataKeys],
          };
        });
      }

      return {
        columns: fmColumns,
        schema: currSchema,
        yAxis: fmYAxis,
      };
    } else if (data.deviceType.name.toLowerCase().includes('ae')) {
      let currSchema;

      if (data.uniqueId.includes('ae-1-1')) {
        currSchema = aeSchemaCustom.map((item) => {
          return {
            ...item,
            dataKeys: ['ae1In', ...item.dataKeys],
          };
        });
      } else if (data.uniqueId.includes('ae-1-2')) {
        currSchema = aeSchemaCustom.map((item) => {
          return {
            ...item,
            dataKeys: ['ae1Out', ...item.dataKeys],
          };
        });
      } else if (data.uniqueId.includes('ae-2-1')) {
        currSchema = aeSchemaCustom.map((item) => {
          return {
            ...item,
            dataKeys: ['ae2In', ...item.dataKeys],
          };
        });
      } else if (data.uniqueId.includes('ae-2-2')) {
        currSchema = aeSchemaCustom.map((item) => {
          return {
            ...item,
            dataKeys: ['ae2Out', ...item.dataKeys],
          };
        });
      } else if (data.uniqueId.includes('ae-1')) {
        currSchema = aeSchema.map((item) => {
          return {
            ...item,
            dataKeys: ['ae1', ...item.dataKeys],
          };
        });
      } else if (data.uniqueId.includes('ae-2')) {
        currSchema = aeSchema.map((item) => {
          return {
            ...item,
            dataKeys: ['ae2', ...item.dataKeys],
          };
        });
      } else if (data.uniqueId.includes('ae-3')) {
        currSchema = aeSchema.map((item) => {
          return {
            ...item,
            dataKeys: ['ae3', ...item.dataKeys],
          };
        });
      }

      let currAeColumns = [...aeColumns];
      let currAeYAxis = [...aeYAxis];
      const deviceUniqueIdMapped = data.uniqueId.split('-');

      if (deviceUniqueIdMapped.length === 4) {
        currAeColumns = [...aeColumnsCustom];
        currAeYAxis = [...aeYAxisCustom];
      }

      return {
        columns: currAeColumns,
        schema: currSchema,
        yAxis: currAeYAxis,
      };
    } else if (data.deviceType.name.toLowerCase() === 'gateway') {
      return {
        columns: gatewayColumns,
        schema: gatewaySchema,
        yAxis: gatewayYAxis,
      };
    }
  }, [data.deviceType]);

  const renderMobileDetailRow = (
    label: string,
    value: React.ReactNode,
    options?: { fullWidth?: boolean },
  ) => (
    <DeviceMobileDetailRow $fullWidth={options?.fullWidth}>
      <DeviceMobileDetailLabel>{label}</DeviceMobileDetailLabel>
      <DeviceMobileDetailValue>{value || 'N/A'}</DeviceMobileDetailValue>
    </DeviceMobileDetailRow>
  );

  const renderMobileInformation = () => (
    <>
      {data?.deletedAt ? (
        <DeviceMobileAlert>
          <Alert
            type="warning"
            message="This data has been archived and cannot be edited"
          />
        </DeviceMobileAlert>
      ) : (
        false
      )}
      <DeviceMobileDetailSection $bordered>
        <DeviceMobileDetailSectionHeader>
          <h2>Information</h2>
          <p>General information about this device</p>
        </DeviceMobileDetailSectionHeader>
        <DeviceMobileDetailRows>
          {renderMobileDetailRow('Name', data.name)}
          {renderMobileDetailRow('Device Type', data.deviceType.name || 'N/A')}
          {renderMobileDetailRow('Unique ID', data.uniqueId || 'N/A', {
            fullWidth: true,
          })}
          {renderMobileDetailRow(
            'Created by',
            data?.userCreator?.name || 'N/A',
          )}
          {renderMobileDetailRow(
            'Created at',
            data.createdAt
              ? moment(data.createdAt).format('DD MMMM YYYY, HH:mm')
              : 'N/A',
          )}
          {renderMobileDetailRow(
            'Updated at',
            data.updatedAt
              ? moment(data.updatedAt).format('DD MMMM YYYY, HH:mm')
              : 'N/A',
          )}
          {data.metadata && data.metadata.aeFuelConstanta
            ? renderMobileDetailRow(
                'AE Fuel Constanta',
                data.metadata.aeFuelConstanta + ' L/Min',
              )
            : null}
          {renderMobileDetailRow(
            'Status',
            <Tag color={data.status === 'active' ? 'success' : 'error'}>
              {data.status?.toUpperCase()}
            </Tag>,
          )}
        </DeviceMobileDetailRows>
      </DeviceMobileDetailSection>

      <DeviceMobileDetailSection>
        <DeviceMobileDetailSectionHeader>
          <h2>Asset</h2>
          <p>Asset connected to this device</p>
        </DeviceMobileDetailSectionHeader>
        <DeviceMobileDetailRows>
          {renderMobileDetailRow('Name', data?.asset?.name || 'N/A')}
          {renderMobileDetailRow(
            'Company',
            data?.asset?.company?.name || 'N/A',
          )}
          {renderMobileDetailRow('PIC Name', data?.asset?.picName || 'N/A')}
          {renderMobileDetailRow(
            'PIC Phone Number',
            data?.asset?.picPhoneNumber || 'N/A',
          )}
        </DeviceMobileDetailRows>
      </DeviceMobileDetailSection>
    </>
  );

  const isPanelDoorDevice =
    data.deviceType.name?.trim().toLowerCase() === 'panel door';

  const items: TabsProps['items'] = useMemo(() => {
    if (data.deviceId && isPanelDoorDevice) {
      return [
        {
          key: '1',
          label: 'Detail Information',
          children: isMobile ? renderMobileInformation() : (
            <DeviceDetailInformation data={data} />
          ),
        },
        {
          key: '2',
          label: 'Data Log',
          children: <PanelDoorDataLog device={data} />,
        },
      ];
    }

    if (data.deviceId && chartData && chartData.columns && chartData.schema) {
      return [
        {
          key: '1',
          label: 'Detail Information',
          children: isMobile ? (
            renderMobileInformation()
          ) : (
            <DeviceDetailInformation data={data} />
          ),
        },
        {
          key: '2',
          label: 'Data Log',
          children: (
            <DeviceDataLog
              columns={chartData.columns}
              schema={chartData.schema}
              yAxis={chartData.yAxis}
              device={data}
              height={isMobile ? 320 : 500}
            />
          ),
        },
      ];
    } else if (data.deviceId) {
      return [
        {
          key: '1',
          label: 'Detail Information',
          children: isMobile ? (
            renderMobileInformation()
          ) : (
            <DeviceDetailInformation data={data} />
          ),
        },
      ];
    }
  }, [data, chartData, isMobile, isPanelDoorDevice]);

  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<string>();

  useEffect(() => {
    const tab = searchParams.get('tab');
    tab && setActiveTab(tab);
  }, [searchParams]);

  return (
    <React.Fragment>
      {isMobile ? (
        <DeviceMobilePage>
          <MobileDeviceHeader
            title="Detail Device"
            onBack={() => backToList(navigate, '/device')}
            action={
              isReadOnlyRamus2 ? undefined : (
                <DeviceMobileActionButton
                  disabled={data.deletedAt ? true : false}
                  type="primary"
                  onClick={handleClickEdit}
                >
                  Edit
                </DeviceMobileActionButton>
              )
            }
          />

          <Spin spinning={isLoading}>
            <DeviceMobileDetailTabs>
              <Tabs
                size="small"
                activeKey={activeTab}
                onChange={(key) => setActiveTab(key)}
                items={items}
              />
            </DeviceMobileDetailTabs>
          </Spin>
        </DeviceMobilePage>
      ) : (
        <>
          <HeaderSection
            icon='back'
            title={'Detail Device ' + data.asset.name + ' - ' + data.name}
            subtitle={`Manage your device data`}
            rightAction={
              isReadOnlyRamus2 ? false : (
                <Space>
                  <Button
                    disabled={data.deletedAt ? true : false}
                    type='primary'
                    onClick={handleClickEdit}
                  >
                    Edit Device
                  </Button>
                </Space>
              )
            }
          />

          <Spin spinning={isLoading}>
            <Tabs
              size='small'
              activeKey={activeTab}
              onChange={(key) => setActiveTab(key)}
              items={items}
            />
          </Spin>
        </>
      )}
    </React.Fragment>
  );
};

export default DeviceDetail;
